// ChangePassword.jsx
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setIsTempPassword } from "../store/slices/authSlice";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Lock, CheckCircle } from "@mui/icons-material";
import { API_BASE_URL } from "../config/api";

export default function ChangePassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { email, forceChange } = location.state || {};
  
  const { token, user } = useSelector((state) => state.auth);
  
  // Get email from state or from redux user object
  const userEmail = email || user?.email;
  
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!userEmail) {
      setError("Email is missing. Please log in again.");
      return;
    }

    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (form.newPassword !== form.confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (form.newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/agents/auth/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: userEmail,
            current_password: form.currentPassword,
            new_password: form.newPassword,
            confirm_password: form.confirmPassword,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Password change failed");
      }

      // Password change successful
      setSuccess(true);
      
      // Update Redux state to reflect temp password has been cleared
      dispatch(setIsTempPassword(false));
      
      // Redirect to chat after 2 seconds
      const { customerId, agentId, agentName } = location.state || {};
      setTimeout(() => {
        navigate("/chat", {
          state: { customerId, agentId, agentName },
        });
      }, 2000);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Box textAlign="center" mb={3}>
          <Lock sx={{ fontSize: 48, color: forceChange ? "warning.main" : "primary.main" }} />
          <Typography variant="h5" fontWeight={600}>
            {forceChange ? "Password Change Required" : "Change Password"}
          </Typography>
          {forceChange && (
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Enter the temporary password from your welcome email, then choose a new password
            </Typography>
          )}
          {/* Display email for verification */}
          {userEmail && (
            <Typography 
              variant="body2" 
              sx={{ 
                mt: 2, 
                p: 1.5, 
                bgcolor: "#E0F2FE",
                borderRadius: 1,
                color: "#0288D1",
                fontWeight: 500
              }}
            >
              Changing password for: <strong>{userEmail}</strong>
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <Box textAlign="center" py={4}>
            <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
            <Typography variant="h6" color="success.main">
              Password changed successfully!
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Redirecting to chat...
            </Typography>
            <CircularProgress sx={{ mt: 3 }} />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              name="currentPassword"
              label={forceChange ? "Temporary / Current Password" : "Current Password"}
              type="password"
              value={form.currentPassword}
              onChange={handleChange}
              disabled={loading}
              margin="normal"
              required
              autoComplete="current-password"
            />
            
            <TextField
              fullWidth
              name="newPassword"
              label="New Password"
              type="password"
              value={form.newPassword}
              onChange={handleChange}
              disabled={loading}
              margin="normal"
              required
              autoComplete="new-password"
            />
            
            <TextField
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              value={form.confirmPassword}
              onChange={handleChange}
              disabled={loading}
              margin="normal"
              required
              autoComplete="new-password"
            />
            
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, py: 1.2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Change Password"}
            </Button>
            
            {!forceChange && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate("/chat")}
                sx={{ mt: 2 }}
              >
                Cancel
              </Button>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
}